import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Pause, Play, ShoppingCart } from "lucide-react";
import { api, resolveCoverUrl } from "@/lib/api";
import { useCart } from "@/lib/CartContext";
import { useCurrency } from "@/lib/CurrencyContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { formatTrackTitle } from "@/lib/trackTitle";
import { useTrackActions } from "@/components/CatalogPrimitives";
import { musicLine, shortKey } from "@/lib/musicAttrs";

const mmss = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "—");
const bpmText = (b) => (b ? `${Math.round(b)} BPM` : "—");
// Fixed columns on every row so mixed Arabic/English titles can never shift BPM/Key/Price.
const GRID = "grid grid-cols-[28px_44px_minmax(0,1fr)_88px] md:grid-cols-[36px_48px_minmax(0,1fr)_76px_128px_52px_96px_36px] items-center gap-3";

function CartButton({ rank, track, actions, inCart }) {
  if (!(track.price > 0) || track.owned) return <span className="h-8 w-8" aria-hidden="true" />;
  return (
    <button type="button" onClick={() => actions.onAddToCart(track)} disabled={inCart} data-testid={`best-seller-cart-${rank}`}
      aria-label="Add to cart"
      className={`h-8 w-8 shrink-0 rounded-full border flex items-center justify-center transition-colors ${inCart ? "border-red-brand text-red-brand" : "border-white/20 text-white/80 hover:border-red-brand hover:text-white"}`}>
      {inCart ? <Check size={14} /> : <ShoppingCart size={14} />}
    </button>
  );
}

function Row({ rank, track, actions, inCart }) {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { format } = useCurrency();
  const playing = actions.playingId === track.id;
  const title = formatTrackTitle(track);
  const align = { textAlign: lang === "ar" ? "right" : "left" };  // page-side alignment, content-side bidi
  const href = track.track_slug ? localisedTo(`/track/${track.track_slug}`, lang) : null;
  const price = track.price > 0 ? format(track.price) : <span className="text-red-brand">{t("trackCard.free")}</span>;
  return (
    <li data-testid={`best-seller-row-${rank}`} className={`group ${GRID} px-3 py-2.5 border-t border-white/5 first:border-t-0 hover:bg-white/[0.03] transition-colors`}>
      <span className={`font-anton text-xl md:text-2xl text-center ${rank <= 3 ? "text-red-brand" : "text-white/40"}`} data-testid={`best-seller-rank-${rank}`}>{rank}</span>
      <button type="button" onClick={() => actions.onPlayToggle(track)} data-testid={`best-seller-play-${rank}`}
        aria-label={playing ? "Pause" : "Play"} className="relative h-11 w-11 md:h-12 md:w-12 overflow-hidden rounded-md bg-white/5">
        <img src={resolveCoverUrl(track.cover_url)} alt="" loading="lazy" className="h-full w-full object-cover" />
        <span className={`absolute inset-0 flex items-center justify-center bg-black/55 transition-opacity ${playing ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </span>
      </button>
      <div className="min-w-0 pe-2 text-start" data-testid={`best-seller-titlecell-${rank}`}>
        {href ? <Link to={href} dir="auto" style={align} className="line-clamp-2 md:block md:line-clamp-none md:truncate text-sm leading-snug text-white hover:text-red-brand" data-testid={`best-seller-title-${rank}`}>{title}</Link>
          : <div dir="auto" style={align} className="line-clamp-2 md:line-clamp-none md:truncate text-sm leading-snug text-white" data-testid={`best-seller-title-${rank}`}>{title}</div>}
        <div className="md:hidden font-mono text-[10px] leading-tight text-white/45 mt-0.5 break-words" data-testid={`best-seller-meta-mobile-${rank}`}><bdi dir="ltr">
          {[musicLine(track), mmss(track.duration)].filter(Boolean).join(" · ")}
        </bdi></div>
      </div>
      <span className="hidden md:block font-mono text-xs text-white/70 text-start" data-testid={`best-seller-bpm-${rank}`}><bdi dir="ltr">{bpmText(track.bpm)}</bdi></span>
      <span className="hidden md:block font-mono text-xs text-white/70 truncate text-start" data-testid={`best-seller-key-${rank}`}>
        <bdi dir="ltr">{track.camelot ? `${shortKey(track.camelot)} · ${track.camelot}` : ""}</bdi>
      </span>
      <span className="hidden md:block font-mono text-xs text-white/50 text-start" data-testid={`best-seller-duration-${rank}`}><bdi dir="ltr">{mmss(track.duration)}</bdi></span>
      <span className="hidden md:block font-anton text-sm text-white whitespace-nowrap text-end" data-testid={`best-seller-price-${rank}`}><bdi dir="ltr">{price}</bdi></span>
      <div className="flex items-center gap-2 justify-end">
        <span className="md:hidden font-anton text-xs text-white whitespace-nowrap" data-testid={`best-seller-price-mobile-${rank}`}><bdi dir="ltr">{price}</bdi></span>
        <CartButton rank={rank} track={track} actions={actions} inCart={inCart} />
      </div>
    </li>
  );
}

// Compact Top 10 (server-ranked, cached). Hidden unless 10 tracks qualify.
export default function HomeBestSellers() {
  const { t } = useTranslation();
  const actions = useTrackActions();
  const cart = useCart();
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/site/best-sellers").then((r) => live && setData(r.data)).catch(() => live && setData({ items: [] }));
    return () => { live = false; };
  }, []);
  if (!data?.items?.length) return null;
  const allTime = data.period === "all";
  return (
    <section data-testid="home-best-sellers" className="px-6 md:px-10 max-w-[1100px] mx-auto my-14">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand" data-testid="home-best-sellers-period">
            {allTime ? t("bestSellers.allTime") : t("bestSellers.lastDays", { days: data.period })}
          </div>
          <h2 className="font-anton text-3xl md:text-4xl text-white mt-1">{t("bestSellers.title")}</h2>
        </div>
        {data.is_demo && (
          <span data-testid="home-best-sellers-demo-badge" role="status"
            className="font-mono text-[11px] md:text-xs tracking-[0.2em] font-bold uppercase text-white bg-red-brand px-3 py-1.5 rounded-sm">
            {t("bestSellers.demo")}
          </span>
        )}
      </div>
      <ol className="rounded-2xl border border-white/10 bg-white/[0.015] overflow-hidden" data-testid="home-best-sellers-list">
        {data.items.map(({ rank, track }) => (
          <Row key={track.id} rank={rank} track={track} actions={actions} inCart={cart.hasId(track.id)} />
        ))}
      </ol>
    </section>
  );
}
